// src/pages/CartPage.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

export default function CartPage() {
  const { cart, totalItems, subtotal, removeFromCart, updateQty } = useCart();
  const navigate = useNavigate();

  const shipping = subtotal >= 500 ? 0 : subtotal > 0 ? 35 : 0;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const cartStyles = `
    @media (max-width: 768px) {
      .cart-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
      .cart-page-header { padding: 24px 16px 18px !important; }
      .cart-page-body { padding: 20px 16px !important; }
    }
    @media (max-width: 640px) {
      .cart-item-row { grid-template-columns: 72px 1fr auto !important; gap: 12px !important; padding: 16px 0 !important; }
      .cart-item-img-wrap { height: 82px !important; }
      .cart-summary-box { padding: 20px 16px !important; }
    }
    @media (max-width: 400px) {
      .cart-item-row { grid-template-columns: 60px 1fr !important; gap: 10px !important; }
    }
  `;

  if (cart.length === 0) return (
    <>
      <style>{cartStyles}</style>
      <div style={{ paddingTop: 68, minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      <div style={{ fontSize: 64, marginBottom: 8 }}>🛍</div>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 32, fontWeight: 400 }}>Your bag is empty</h2>
      <p style={{ color: 'var(--text-3)', maxWidth: 360, textAlign: 'center' }}>Start exploring our curated food and luxury shopping collections.</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <Link to="/shop?type=food" className="btn btn-emerald">Order Food</Link>
        <Link to="/shop?type=shopping" className="btn btn-gold">Shop Luxury</Link>
      </div>
    </div>
    </>
  );

  return (
    <>
      <style>{cartStyles}</style>
      <div style={{ paddingTop: 68 }}>
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: 'clamp(20px, 5vw, 44px) clamp(16px, 5vw, 40px) clamp(16px, 3vw, 32px)' }}>
        <div className="container" style={{ padding: 0 }}>
          <p className="section-eyebrow">Your Selection</p>
          <h1 className="section-title">Shopping Bag ({totalItems})</h1>
        </div>
      </div>

      <div className="container" style={{ padding: 'clamp(20px, 4vw, 44px) clamp(16px, 4vw, 40px)' }}>
        <div className="cart-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 40, alignItems: 'start' }}>
          {/* Items */}
          <div>
            {cart.map((item, i) => (
              <div key={item._key} style={{ display: 'grid', gridTemplateColumns: '88px 1fr auto', gap: 16, padding: '20px 0', borderBottom: '1px solid var(--border)', animation: `fade-up 0.4s ease ${i * 0.05}s both` }}>
                <Link to={`/product/${item.id}`}>
                  <div style={{ height: 110, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                </Link>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 5 }}>
                    <span className={`badge ${item.type === 'food' ? 'badge-emerald' : 'badge-gold'}`} style={{ fontSize: 9 }}>
                      {item.type === 'food' ? '🍽 Food' : '✦ Luxury'}
                    </span>
                  </div>
                  <Link to={`/product/${item.id}`} style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 400, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                    {item.name}
                  </Link>
                  {(item.selectedSize || item.selectedColor) && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>
                      {[item.selectedSize, item.selectedColor].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 4, width: 'fit-content' }}>
                    <button onClick={() => item.qty === 1 ? removeFromCart(item._key) : updateQty(item._key, item.qty - 1)}
                      style={{ width: 36, height: 36, background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>−</button>
                    <span style={{ width: 36, textAlign: 'center', fontSize: 13 }}>{item.qty}</span>
                    <button onClick={() => updateQty(item._key, item.qty + 1)}
                      style={{ width: 36, height: 36, background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>+</button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, color: 'var(--gold)' }}>
                    ${(item.price * item.qty).toLocaleString()}
                  </span>
                  <button onClick={() => removeFromCart(item._key)} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 11, letterSpacing: '0.05em', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--red)'}
                    onMouseLeave={e => e.target.style.color = 'var(--text-3)'}
                  >Remove</button>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <Link to="/shop?type=food" className="btn btn-ghost btn-sm">+ Add Food</Link>
              <Link to="/shop?type=shopping" className="btn btn-ghost btn-sm">+ Add Items</Link>
            </div>
          </div>

          {/* Summary */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 28, position: 'sticky', top: 90 }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, marginBottom: 24 }}>Order Summary</h3>

            {subtotal < 500 && (
              <div style={{ background: 'rgba(212,168,83,0.07)', border: '1px solid var(--border)', borderRadius: 4, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--text-2)' }}>
                📦 Add ${(500 - subtotal).toFixed(0)} more for free shipping!
              </div>
            )}

            {[['Subtotal', `$${subtotal.toFixed(2)}`], ['Shipping', shipping === 0 ? '✓ Free' : `$${shipping}`], ['Tax (8%)', `$${tax.toFixed(2)}`]].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 13 }}>
                <span style={{ color: 'var(--text-2)' }}>{k}</span>
                <span style={{ color: v.startsWith('✓') ? 'var(--green)' : 'var(--text)' }}>{v}</span>
              </div>
            ))}

            <div style={{ height: 1, background: 'var(--border)', margin: '18px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 17 }}>Total</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, color: 'var(--gold)' }}>${total.toFixed(2)}</span>
            </div>

            <button onClick={() => navigate('/checkout')} className="btn btn-gold btn-full btn-lg">
              Proceed to Checkout →
            </button>
            <Link to="/shop" style={{ display: 'flex', justifyContent: 'center', marginTop: 14, fontSize: 11, letterSpacing: '0.08em', color: 'var(--text-3)', textTransform: 'uppercase', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = 'var(--gold)'}
              onMouseLeave={e => e.target.style.color = 'var(--text-3)'}
            >Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
